import React, { useState } from 'react';
import CompareShell, { AppStoreBadges, IOS_INVITE_URL } from './CompareShell';
import { Check, X, Users, DollarSign, CreditCard, ArrowRight } from 'lucide-react';

const VENMO_MATRIX = [
  {
    category: 'Clero Groups & Shared Spending',
    features: [
      {
        name: 'Monthly roommate groups (rent & bills)',
        clero: 'Auto-settles on the 1st',
        competitor: 'Not supported (1-on-1 only)',
        cleroWinner: true,
      },
      {
        name: 'Trip & vacation groups',
        clero: 'Auto-settles on trip end date',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'On-demand running group tabs',
        clero: '1-tap settle anytime',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'Group balance simplification',
        clero: 'Included ($0 free)',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: '1-tap batch payment for all requests',
        clero: '1 bank transfer',
        competitor: 'Authorize 1-by-1',
        cleroWinner: true,
      },
      {
        name: 'Locked payment records',
        clero: 'Permanently locked',
        competitor: 'Not supported',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Transfers & Fees',
    features: [
      {
        name: 'Instant cashout fee',
        clero: '$0 always',
        competitor: '1.75% (up to $25/transfer)',
        cleroWinner: true,
      },
      {
        name: 'Where your money lands',
        clero: 'Direct to checking account',
        competitor: 'Trapped in app wallet balance',
        cleroWinner: true,
      },
      {
        name: 'Standard transfer time',
        clero: 'Same-day direct deposit*',
        competitor: '1 to 3 business days',
        cleroWinner: true,
      },
      {
        name: 'Average yearly fee savings',
        clero: 'Save ~$252/year',
        competitor: 'Fees deducted on every cashout',
        cleroWinner: true,
      },
      {
        name: 'Multi-bank & card linking',
        clero: 'Multiple accounts & cards',
        competitor: 'Single primary balance',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Receipts & Item Splitting',
    features: [
      {
        name: 'AI camera receipt scanner',
        clero: 'Free itemized claiming',
        competitor: 'Manual typing required',
        cleroWinner: true,
      },
      {
        name: 'Gmail & email receipt sync',
        clero: 'Auto-finds Uber & food orders',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'Smart card charge requests',
        clero: '1-tap from transactions',
        competitor: 'Manual entry',
        cleroWinner: true,
      },
      {
        name: 'Friend web payment portal',
        clero: 'Pay on web without app',
        competitor: 'Requires Venmo app',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Privacy & Security',
    features: [
      {
        name: 'Public social transaction feed',
        clero: 'None (100% private)',
        competitor: 'Public feed default',
        cleroWinner: true,
      },
      {
        name: 'Biometric security',
        clero: 'Face ID & Touch ID',
        competitor: 'Optional PIN',
        cleroWinner: true,
      },
    ],
  },
];

const PRESET_VOLUMES = [
  { label: 'Casual ($600)', value: 600 },
  { label: 'Rent & Bills ($1,200)', value: 1200 },
  { label: 'Roommates ($2,400)', value: 2400 },
  { label: 'Frequent ($3,600)', value: 3600 },
];

export const CompareVenmoPage: React.FC = () => {
  const [monthlyVolume, setMonthlyVolume] = useState<number>(1200);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  };

  // Math for fee calculation (Venmo 1.75% instant cashout penalty)
  const venmoMonthlyFee = Math.round(monthlyVolume * 0.0175);
  const venmoYearlyFee = venmoMonthlyFee * 12;

  return (
    <CompareShell competitorName="Venmo" competitorSlug="venmo">
      {/* STRUCTURED JSON-LD SEO SCHEMA */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FinancialProduct',
            name: 'Clero vs Venmo Comparison & Fee Calculator',
            description:
              'Calculate your annual savings with Clero vs Venmo 1.75% instant transfer fees. Direct bank deposits, AI receipts, and group payments for $0.',
            provider: {
              '@type': 'Organization',
              name: 'Clero',
              url: 'https://clero.money',
            },
            offers: {
              '@type': 'Offer',
              price: '0.00',
              priceCurrency: 'USD',
            },
          }),
        }}
      />

      {/* HERO SECTION */}
      <section className="pt-6 pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-slate-950">
            Clero vs Venmo
          </h1>
          <p className="mx-auto max-w-xl text-base sm:text-lg text-slate-600 font-medium">
            See how Clero compares to Venmo for everyday and group payments.
          </p>

          {/* HERO DOWNLOAD ACTION (FLIGHTY STYLE) */}
          <div className="pt-2 flex items-center justify-center">
            <AppStoreBadges badgeHeightClass="h-11 sm:h-12" />
          </div>
        </div>
      </section>

      {/* SIMPLIFIED, AIRY INSTANT FEE SAVINGS CALCULATOR */}
      <section aria-labelledby="venmo-fee-calculator-heading" className="py-8 px-4 sm:px-6" id="venmo-fee-calculator">
        <div className="mx-auto max-w-5xl">
          <div className="rounded-3xl bg-white p-8 sm:p-12">
            <div className="text-center max-w-xl mx-auto mb-10">
              <h2 id="venmo-fee-calculator-heading" className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                Calculate your fee savings
              </h2>
              <p className="mt-2 text-sm text-slate-600 font-medium">
                Venmo takes 1.75% every time you cash out immediately. Clero deposits directly to checking on the same day* for $0.
              </p>
            </div>

            {/* Clean Centered Slider & Value Display */}
            <div className="max-w-2xl mx-auto space-y-8">
              <div className="rounded-2xl bg-slate-50 p-6 sm:p-8 space-y-5 text-center">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                  Monthly shared expenses & rent
                </span>

                <div className="text-4xl sm:text-5xl font-black text-slate-950 tracking-tight">
                  ${monthlyVolume.toLocaleString()}
                  <span className="text-sm font-semibold text-slate-400 ml-1">/ month</span>
                </div>

                <div className="px-2 pt-2">
                  <input
                    aria-label="Monthly Transfer Volume"
                    className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-teal-600"
                    id="venmo-volume-slider"
                    max={5000}
                    min={200}
                    onChange={(e) => setMonthlyVolume(Number(e.target.value))}
                    step={100}
                    type="range"
                    value={monthlyVolume}
                  />
                  <div className="mt-2 flex justify-between text-xs font-semibold text-slate-400">
                    <span>$200/mo</span>
                    <span>$2,500/mo</span>
                    <span>$5,000/mo</span>
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="flex flex-wrap justify-center gap-2 pt-2">
                  {PRESET_VOLUMES.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setMonthlyVolume(preset.value)}
                      className={`rounded-xl px-4 py-2 text-xs font-bold transition-all ${
                        monthlyVolume === preset.value
                          ? 'bg-slate-950 text-white shadow-sm'
                          : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/60'
                      }`}
                      type="button"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clean Result Card (Airy, bold, uncluttered) */}
              <div className="rounded-2xl bg-teal-50/80 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
                <div>
                  <span className="text-xs font-extrabold uppercase tracking-wider text-teal-800 block">
                    You save with Clero
                  </span>
                  <div className="mt-1 text-3xl sm:text-4xl font-black text-teal-950 tracking-tight">
                    ${venmoYearlyFee.toLocaleString()}
                    <span className="text-sm font-bold text-teal-800 font-sans ml-1.5">every year</span>
                  </div>
                  <p className="mt-1 text-xs text-teal-900/80 font-medium">
                    Based on 1.75% instant cashout penalties on ${monthlyVolume.toLocaleString()}/mo
                  </p>
                </div>

                <div className="shrink-0">
                  <a
                    href={IOS_INVITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-teal-700 px-5 py-2.5 text-xs font-bold text-white hover:bg-teal-800 active:scale-95 transition-all shadow-sm"
                  >
                    <span>Keep your money</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FULL COMPARISON MATRIX SECTION */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl">
          {/* COMPREHENSIVE FLIGHTY-STYLE WRITE-UP */}
          <div className="rounded-3xl bg-white p-8 sm:p-12 mb-10 space-y-8">
            <div className="border-b border-slate-100 pb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                Direct bank checking settlement without middleman wallet fees
              </h2>
              <p className="mt-3 text-base text-slate-600 font-medium leading-relaxed">
                Venmo transformed personal payments by making 1-on-1 transfers fast and casual. But its core business model depends on keeping your funds inside an app balance and charging instant transfer fees to access your own money.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">1. $0 cashout fees vs 1.75% penalties</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Venmo holds incoming funds in an app wallet. Moving cash to your bank immediately costs 1.75% (up to $25 per transfer), draining ~$252 a year for active roommates. Clero moves payments directly into your checking account on the same day* for $0.
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">2. Group intelligence vs 1-on-1 math</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Venmo only handles simple 1-on-1 payments. When multiple roommates or travelers incur shared costs, you are stuck sending dozens of individual requests. Clero automatically nets group debts so each person only makes or receives one single payment.
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">3. Smart card sync & web claiming</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Instead of manually typing amounts from memory, Clero connects to credit cards and Gmail to pull posted Uber rides and grocery bills with 1 tap. Friends can claim exact items and pay via a web link without needing an app install.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
              Feature breakdown
            </h2>
            <p className="mt-2 text-sm text-slate-600 font-medium">
              Side-by-side comparison between Clero and Venmo.
            </p>
          </div>

          <div className="overflow-hidden rounded-3xl bg-white">
            {/* TABLE TOP HEADER WITH APP LOGOS */}
            <div className="grid grid-cols-12 gap-3 px-6 py-4 border-b border-slate-100 items-center bg-slate-50/50">
              <div className="col-span-6 sm:col-span-5">
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Features</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <img src="/clero_icon.png" alt="Clero" className="h-6 w-6 rounded-lg shrink-0 object-cover shadow-sm" />
                <span className="font-extrabold text-teal-950 text-xs sm:text-sm">Clero</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <img src="/logos/venmo-mark.svg" alt="Venmo" className="h-6 w-6 rounded-lg shrink-0 shadow-sm" />
                <span className="font-bold text-slate-800 text-xs sm:text-sm">Venmo</span>
              </div>
            </div>

            {/* MATRIX CATEGORIES & CONCISE ROWS */}
            {VENMO_MATRIX.map((section) => (
              <div key={section.category}>
                <div className="bg-slate-100/70 px-6 py-2.5">
                  <h3 className="text-[11px] font-extrabold uppercase tracking-wider text-slate-800">
                    {section.category}
                  </h3>
                </div>

                <div className="divide-y divide-slate-100">
                  {section.features.map((row) => (
                    <div
                      key={row.name}
                      className="grid grid-cols-12 gap-3 px-6 py-3.5 items-center hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="col-span-6 sm:col-span-5">
                        <span className="text-xs sm:text-sm font-bold text-slate-900 block">{row.name}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 bg-teal-50/40 p-2 rounded-xl">
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
                          <Check className="h-2.5 w-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs font-bold text-teal-950">{row.clero}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 p-2">
                        {row.cleroWinner ? (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <X className="h-2.5 w-2.5 stroke-[2.5]" />
                          </div>
                        ) : (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                            <Check className="h-2.5 w-2.5 stroke-[3]" />
                          </div>
                        )}
                        <span className="text-xs font-medium text-slate-600">{row.competitor}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* MID-PAGE CONVERSION CALLOUT (FLIGHTY STYLE) */}
          <div className="mt-8 rounded-3xl bg-white p-6 sm:p-8 text-center space-y-3">
            <h3 className="text-lg sm:text-xl font-black text-slate-950">
              Ready for same-day* deposits?
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
              $0 instant fees, AI receipt parsing, and group expense netting directly to checking.
            </p>
            <div className="flex justify-center pt-1">
              <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
            </div>
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS ABOUT VENMO & CLERO (FLIGHTY STYLE) */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl rounded-3xl bg-white p-7 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-6 text-sm sm:text-base text-slate-700 font-medium divide-y divide-slate-100">
            <div className="pt-4 first:pt-0">
              <h3 className="text-base font-bold text-slate-950">How does Clero save me money compared to Venmo?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Venmo charges a 1.75% penalty (minimum $0.25, maximum $25) whenever you cash out to your bank card immediately. On $1,200 of monthly rent, shared groceries, and dining, that adds up to ~$252 each year. Clero deposits funds straight into your checking account on the same day* with $0 transfer and cashout fees.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">How does bill splitting in Clero differ from Venmo requests?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Venmo requires you to calculate amounts manually and send separate one-by-one requests. Clero scans receipts with AI, splits tax and tip proportionally to the exact cent, and lets groups of roommates or friends settle all open debts in one batch bank transfer.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Is my money held in a middleman app balance?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. Clero does not maintain an intermediate wallet balance. All transfers move directly between bank accounts using Plaid and Astra Financial rails.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Is my transaction activity private?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Yes. Clero has no public social feed or broadcasting. Your payments, roommate bills, and group balances remain 100% private between you and your group members.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">How long does a standard bank transfer take in Clero?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Standard transfers in Clero initiate directly to your bank account with same-day* settlement when initiated before 2:00 PM EST on banking business days (transfers after 2:00 PM EST or over weekends/holidays settle on the next business day), avoiding the 1 to 3 business day delays common on free digital wallet cashouts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER COMPARISONS QUICK JUMP */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <span className="text-xs font-extrabold tracking-widest uppercase text-slate-500">
            More comparisons
          </span>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/compare/splitwise')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Splitwise</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
            <button
              onClick={() => navigate('/compare/zelle')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Zelle</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
          </div>
        </div>
      </section>
    </CompareShell>
  );
};
export default CompareVenmoPage;
